import { BrowserId, idString } from 'Browser/BrowserId';
import FileTypes from 'Browser/FileTypes';
import {Environment} from 'Common/Environment';
import { SiteBasics } from 'Common/SiteBasics';
import { IStoreWrap } from 'Shared/StoreProvider';
import * as imageSize from 'Shared/model/ImageSizes';
import { ImageSchema, ImageSize } from 'Shared/model/basic';
import { fontsAssetFolder } from 'Shared/model/venueSettings';


export interface FullLink
{
	url:string;
	id?:string;
}

export interface PartialLink
{
	partial:string;
	id?:string;
}

export interface ILinks
{
	js: FullLink[];
	css: FullLink[];
	prefixes: {resources?:string};
}


export class Urls
{
	constructor(
		private build:Environment,
		private site:SiteBasics<BrowserId>
	) { }

	imageUrl(folder:string,isShared:boolean,hash:string,size:string,fileType:string,isDev:boolean,proxyProductionAssets:boolean)
	{
		return rawImageUrl(this.build,isShared,this.site.key,folder,hash,size,fileType,isDev,proxyProductionAssets,this.site.assetHost);
	}


	/* Fonts are served cross-origin only with CORS headers, so production fonts are proxied in dev */
	fontUrl(hash:string,format:string,isDev:boolean)
	{
		return rawImageUrl(this.build,false,this.site.key,fontsAssetFolder,hash,'font',format,isDev,true,this.site.assetHost);
	}


//XXX more of a utility function for calling imageUrl? Can I remove or improve?
	createVenueImageUrl(hash:string,fieldData:ImageSchema|undefined,assetFolder:string,imageSize:ImageSize,proxyProductionAssets:boolean=false)
	{
		if (fieldData?.formats==undefined || hash==undefined )
			return undefined;

		const size = fieldData.formats[imageSize];
        if (size==null) 
			return undefined;

		return rawImageUrl(this.build,false,this.site.key,assetFolder,hash,imageSize,size.format,fieldData['dev'] ?? false,proxyProductionAssets,this.site.assetHost);
	}


	uploadImageUrl(sw:IStoreWrap<any>,folderPermission:string,formats:keyof typeof imageSize,shared:boolean=false):string
	{
		const encPage = encodeURIComponent(sw.page);
		const encLocation = '/'+encodeURIComponent(JSON.stringify(sw.location));

		/* shared stores the asset under __SHARED rather than the request's site key — must
		   match the read URL (imageUrl(...,isShared,...)). */
		const query = shared ? '?siteKey=__SHARED' : '';

		return `/admin/imageUpload/${encPage}/${sw.permission}/${folderPermission}/${formats}/${idString(sw.docId)}${encLocation}${query}`;
	}

	resourceUrl(relativeUrl:string):string
	{
		return this.buildUrl(`resources/${relativeUrl}`);
	}

	buildUrl(relativeUrl:string):string
	{
		const buildBit = this.build.storeForBuiltAssets=='production' ? '_build' : '_devBuild';
		return `/${buildBit}/`+this.build.key+'/'+relativeUrl;
	}
}


/* Some test systems use a mix of dev images and production images (readonly). */
export function rawImageUrl(build:Environment,isShared:boolean,siteKey:string,folder:string,hash:string,size:string,fileType:string,isDev:boolean,proxyProductionAssets:boolean,assetHost:string='')
{
	let source = `/_assets`;

	if (build.key=='DEV') {
		source = `/_devAssets`;
		if (!isDev)
			source = proxyProductionAssets ? `/_proxyAssets` : `https://dummy.ontoit.xyz/_assets`;
	}
	else if (build.storeForNewAssets=='local' && isDev)
		/* Should be in a build or live test system here, not in prodution */
		source = `/_devAssets`;

	const key = isShared ? '__SHARED' : siteKey;

//TODO  Maybe stored shared images in 'shared' instead?
	const path = source+'/'+key+'/'+folder+'/'+hash+'-'+size+'.'+FileTypes.suffix(fileType);

	/* When the page is served from a different origin than the assets (admin viewing a venue's
	   poster), make relative URLs absolute against the venue host. Already-absolute sources
	   (eg https://dummy.ontoit.xyz/_assets) are left untouched. */
	return assetHost && path.startsWith('/') ? assetHost+path : path;
}

